/**
 * Session upgrade hook.
 * This file lives outside brightback-auth.slice.ts to break the circular
 * dependency between it and preauth-company.slice.ts.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isUnauthorizedError } from '@bbkAdminRedux/rtkq/auth-status';
import { selectAuthStatus } from '@bbkAdminRedux/rtkq/auth-utils';
import { brightbackAuthSlice } from '@bbkAdminRedux/rtkq/brightback-auth.slice';

/** Attempts allowed per sign-in, including the first. */
const MAX_ATTEMPTS = 3;

/** 1s, 2s: doubling, so a provider having a moment is not hammered. */
const RETRY_BASE_MS = 1_000;

/**
 * Moves a session that predates the backend-owned cookie onto the HttpOnly
 * attributes, and hydrates the session metadata the app branches on.
 *
 * There is no timer keeping the session alive here on purpose. The backend
 * extends the cookie on the requests the app is already making, so nothing in
 * the browser has to ask for it.
 *
 * The retry below is not about the session staying alive — it is about the
 * metadata arriving at all. Routing that is specific to Chargebee sessions
 * reads it (including trial-signup continuation), and it is only ever written
 * on the way in, so a single 503 here would otherwise leave the app running
 * without it until the user happened to reload.
 */
export const useUpgradeSession = () => {
  const [upgradeSession$] =
    brightbackAuthSlice.endpoints.upgradeSession.useMutation();

  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const doneRef = useRef(false);

  const upgrade = useCallback(async (): Promise<void> => {
    attemptsRef.current += 1;
    try {
      await upgradeSession$().unwrap();
      doneRef.current = true;
    } catch (error) {
      // A 401 means there is no session to upgrade, which retrying cannot
      // change. The unauthorized-redirect middleware owns that outcome.
      if (isUnauthorizedError(error) || attemptsRef.current >= MAX_ATTEMPTS) {
        doneRef.current = true;
        return;
      }
      const delay = RETRY_BASE_MS * 2 ** (attemptsRef.current - 1);
      timerRef.current = setTimeout(() => void upgrade(), delay);
    }
  }, [upgradeSession$]);

  // Runs once the current-user probe confirms a session, not on mount, because
  // upgrading before then would have nothing to upgrade.
  const authStatus = useSelector(selectAuthStatus);
  useEffect(() => {
    if (
      authStatus === 'authenticated' &&
      !doneRef.current &&
      attemptsRef.current === 0
    ) {
      void upgrade();
    }
  }, [authStatus, upgrade]);

  useEffect(() => () => clearTimeout(timerRef.current), []);
};
